// src/pages/work/WorkDetails.ui.mainbody.jsx
// WorkDetails Main Body — presentational only (no data fetching/toasts)
// Sections: Overview • Client & Location • Type-specific (Task/Job)
// NOTE: Schedule section and tiny overview pills removed per latest request.

import React, { useEffect, useRef, useState } from "react";
import { Edit3, MapPin, Phone, Mail } from "lucide-react";
import { safeCurrency } from "../../utils/money";

/* ------------------------------- helpers ---------------------------------- */
const cx = (...a) =>
  a
    .flatMap((x) =>
      typeof x === "string" ? x : x && typeof x === "object" ? Object.keys(x).filter((k) => x[k]) : []
    )
    .join(" ")
    .trim();

function prettyStatus(s) {
  if (!s) return "";
  return String(s)
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

const fmtMoney = (n, c = "USD") =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString(undefined, {
    style: "currency",
    currency: c || "USD",
  });

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

const humanizeDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

const Section = ({ title, actions, children, className }) => (
  <section className={cx("rounded-2xl border border-slate-200 bg-white p-5 shadow-sm", className)}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {actions}
    </div>
    {children}
  </section>
);

/* ---------------------------- Address helpers ----------------------------- */
// tolerant getters so address can be line1/line2/... or street/postalCode/zip
const g = (a, key) => {
  if (!a) return "";
  const map = {
    line1: ["line1", "street", "street1", "address1", "addressLine1"],
    line2: ["line2", "street2", "address2", "unit", "suite", "apartment", "addressLine2"],
    city: ["city", "town"],
    state: ["state", "province", "region", "stateProvince"],
    postal: ["postal", "postalCode", "zip", "zipcode"],
    country: ["country"],
    lat: ["lat", "latitude"],
    lng: ["lng", "lon", "long", "longitude"],
    label: ["label", "name"],
  }[key] || [key];
  for (const k of map) {
    const v = a[k];
    if (v != null && v !== "") return v;
  }
  return "";
};

const joinParts = (arr, sep = ", ") =>
  arr
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean)
    .join(sep);

function fullAddressLine(addr) {
  if (!addr) return "";
  const line1 = g(addr, "line1");
  const line2 = g(addr, "line2");
  const city = g(addr, "city");
  const state = g(addr, "state");
  const postal = g(addr, "postal");
  const country = g(addr, "country");
  return joinParts([line1, line2, joinParts([city, state, postal], " "), country]);
}

function multiLineAddress(addr) {
  if (!addr) return [];
  const out = [];
  const line1 = g(addr, "line1");
  const line2 = g(addr, "line2");
  const city = g(addr, "city");
  const state = g(addr, "state");
  const postal = g(addr, "postal");
  const country = g(addr, "country");
  if (line1) out.push(line1);
  if (line2) out.push(line2);
  const cityLine = joinParts([city, state, postal], " ");
  if (cityLine) out.push(cityLine);
  if (country) out.push(country);
  return out;
}

function sameAddress(a, b) {
  const norm = (x) => fullAddressLine(x).toLowerCase().replace(/\s+/g, " ").trim();
  return !!a && !!b && norm(a) === norm(b) && norm(a) !== "";
}

/** Treat {} or just-label as "no address" */
function hasAddress(a) {
  if (!a || typeof a !== "object") return false;
  return Boolean(
    g(a, "line1") ||
      g(a, "line2") ||
      g(a, "city") ||
      g(a, "state") ||
      g(a, "postal") ||
      g(a, "country") ||
      (g(a, "lat") && g(a, "lng"))
  );
}

// robust pickers (snapshot → live)
const pickClientField = (snap = {}, live = {}, key) => snap?.[key] ?? live?.[key] ?? "";

const pickClientAddress = (snap = {}, live = {}) => {
  const snapAddr =
    snap?.address ??
    snap?.primaryAddress ??
    snap?.billingAddress ??
    (Array.isArray(snap?.addresses) ? snap.addresses[0] : null);

  const liveAddr =
    live?.address ??
    live?.primaryAddress ??
    live?.billingAddress ??
    (Array.isArray(live?.addresses) ? live.addresses[0] : null);

  return snapAddr || liveAddr || null;
};

/* ---------------------- Inline Work Address Editor ------------------------ */
function WorkAddressInlineEditor({ initial, clientAddr, canEdit, onSaveAddress }) {
  // START COLLAPSED even if empty — per spec
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    label: g(initial, "label"),
    line1: g(initial, "line1"),
    line2: g(initial, "line2"),
    city: g(initial, "city"),
    state: g(initial, "state"),
    postal: g(initial, "postal"),
    country: g(initial, "country"),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // keep form in sync when initial changes
  useEffect(() => {
    setForm({
      label: g(initial, "label"),
      line1: g(initial, "line1"),
      line2: g(initial, "line2"),
      city: g(initial, "city"),
      state: g(initial, "state"),
      postal: g(initial, "postal"),
      country: g(initial, "country"),
    });
    // remain collapsed unless user opens it
    setEditing(false);
    setError("");
  }, [initial]);

  const disabled = !canEdit || saving;

  const useClientAddress = () => {
    if (!clientAddr) return;
    setForm((f) => ({
      ...f,
      line1: g(clientAddr, "line1"),
      line2: g(clientAddr, "line2"),
      city: g(clientAddr, "city"),
      state: g(clientAddr, "state"),
      postal: g(clientAddr, "postal"),
      country: g(clientAddr, "country"),
    }));
  };

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!onSaveAddress || !canEdit) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        label: (form.label || "").trim() || undefined,
        line1: (form.line1 || "").trim(),
        line2: (form.line2 || "").trim(),
        city: (form.city || "").trim(),
        state: (form.state || "").trim(),
        postal: (form.postal || "").trim(),
        country: (form.country || "").trim(),
      };
      await onSaveAddress(payload);
      setEditing(false);
    } catch {
      setError("Failed to save address.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      label: g(initial, "label"),
      line1: g(initial, "line1"),
      line2: g(initial, "line2"),
      city: g(initial, "city"),
      state: g(initial, "state"),
      postal: g(initial, "postal"),
      country: g(initial, "country"),
    });
    setEditing(false);
    setError("");
  };

  // Display-only when we have an address and not editing
  if (!editing && hasAddress(initial)) {
    return (
      <>
        {g(initial, "label") ? <div className="text-xs text-slate-500">Label: {g(initial, "label")}</div> : null}
        <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:px-5 md:py-4 border-l-4 border-slate-200">
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <MapPin size={14} /> Site Address
          </div>
          <div className="text-[0.95rem] leading-6 text-slate-800">
            {multiLineAddress(initial).map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </div>
        {canEdit ? (
          <div className="mt-2">
            <button
              type="button"
              className="text-xs text-indigo-700 hover:underline"
              onClick={() => setEditing(true)}
            >
              Edit Location
            </button>
          </div>
        ) : null}
      </>
    );
  }

  // Collapsed CTA when empty & not editing
  if (!editing && !hasAddress(initial)) {
    return canEdit ? (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <MapPin size={14} /> Add site address
      </button>
    ) : (
      <div className="text-xs text-slate-500">No site address.</div>
    );
  }

  // Editor
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-900">{hasAddress(initial) ? "Edit Site Address" : "Add Site Address"}</div>
        {!!clientAddr && (
          <button
            type="button"
            onClick={useClientAddress}
            className="text-xs text-indigo-700 hover:underline"
            disabled={disabled}
            title="Use client address"
          >
            Use client address
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-600">Label (optional)</label>
          <input
            value={form.label}
            onChange={(e) => handleChange("label", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="e.g., Job site, Basement, Unit 304"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-600">Address line 1</label>
          <input
            value={form.line1}
            onChange={(e) => handleChange("line1", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="123 Main St"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-600">Address line 2 (optional)</label>
          <input
            value={form.line2}
            onChange={(e) => handleChange("line2", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Unit, Apt, Suite"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">City</label>
          <input
            value={form.city}
            onChange={(e) => handleChange("city", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">State / Province</label>
          <input
            value={form.state}
            onChange={(e) => handleChange("state", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Postal code</label>
          <input
            value={form.postal}
            onChange={(e) => handleChange("postal", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Country</label>
          <input
            value={form.country}
            onChange={(e) => handleChange("country", e.target.value)}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Canada"
          />
        </div>
      </div>

      {error ? <div className="mt-2 text-xs text-rose-600">{error}</div> : null}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save address"}
        </button>
        <button type="button" onClick={handleCancel} disabled={saving} className="text-xs text-slate-700 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ Main component ---------------------------- */
export default function WorkDetailsMainBody(props) {
  const {
    // data/flags
    work,
    resolving,
    isLoading,
    isError,
    softDeleted,
    canMutate,

    // 🔹 NEW: prefer the fully-fetched client passed by the Shell/Page
    client: clientProp,
    loadingClient,

    // handlers
    onOpenEntity, // (type, id) => void
    onSaveBasics, // ({ title, priority? }) => Promise<void>
    onSaveDescription, // (text) => Promise<void>
    onSaveJobProfile, // (payload) => Promise<void>
    onSaveAddress, // (addressPayload) => Promise<void> (optional, enables inline editor)
  } = props;

  const canEdit = canMutate && !softDeleted;

  // 🔹 Resolve currency: job override → company preference → fallback
  const companySource = work?.companySnapshot || work?.company || work?.tenant || undefined;
  const companyCurrency = safeCurrency(companySource);
  const rawCurrency = work?.jobProfile?.pricing?.currency || companyCurrency || "USD";
  const currency = typeof rawCurrency === "string" ? rawCurrency.toUpperCase() : "USD";

  /* ------------------------------- Local state ------------------------------ */
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleSaving, setTitleSaving] = useState(false);
  const [descSaving, setDescSaving] = useState(false);

  // Job profile (optional inline edit)
  const [editingJob, setEditingJob] = useState(false);
  const [jpServiceType, setJpServiceType] = useState("");
  const [jpOnsite, setJpOnsite] = useState(false);
  const [jpCurrency, setJpCurrency] = useState(currency);
  const [jpExpected, setJpExpected] = useState("");
  const [jpRespBy, setJpRespBy] = useState("");
  const [jpResolveBy, setJpResolveBy] = useState("");
  const [jpMaterials, setJpMaterials] = useState([]);

  const lastSavedTitleRef = useRef("");
  const lastSavedDescRef = useRef("");

  const titleTimerRef = useRef(null);
  const descTimerRef = useRef(null);

  const DEBOUNCE_MS = 800;

  // hydrate from work
  useEffect(() => {
    if (!work) return;
    const t = work.title || "";
    const d = work.description || "";
    setTitle(t);
    setDescription(d);
    lastSavedTitleRef.current = t;
    lastSavedDescRef.current = d;

    if (work.type === "job") {
      const jp = work.jobProfile || {};
      setJpServiceType(jp.serviceType || "");
      setJpOnsite(!!jp.onsiteRequired);
      setJpCurrency(jp?.pricing?.currency || currency);
      setJpExpected(jp?.pricing?.expectedAmount != null ? String(jp.pricing.expectedAmount) : "");
      setJpRespBy(jp?.sla?.responseBy ? toLocalInput(jp.sla.responseBy) : "");
      setJpResolveBy(jp?.sla?.resolveBy ? toLocalInput(jp.sla.resolveBy) : "");
      setJpMaterials(Array.isArray(jp.materials) ? jp.materials.map((m) => ({ ...m })) : []);
    }

    return () => {
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
      if (descTimerRef.current) clearTimeout(descTimerRef.current);
    };
  }, [work, currency]);

  /* ------------------------------- Autosave (Title) ------------------------ */
  useEffect(() => {
    if (!work || !canEdit || typeof onSaveBasics !== "function") return;
    if (title.trim() === (lastSavedTitleRef.current || "").trim()) return;

    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    titleTimerRef.current = setTimeout(async () => {
      try {
        setTitleSaving(true);
        await onSaveBasics({ title });
        lastSavedTitleRef.current = title;
      } finally {
        setTitleSaving(false);
      }
    }, DEBOUNCE_MS);

    return () => titleTimerRef.current && clearTimeout(titleTimerRef.current);
  }, [title, canEdit, onSaveBasics, work]);

  const onTitleBlur = async () => {
    if (!canEdit || typeof onSaveBasics !== "function") return;
    if (title.trim() === (lastSavedTitleRef.current || "").trim()) return;
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    try {
      setTitleSaving(true);
      await onSaveBasics({ title });
      lastSavedTitleRef.current = title;
    } finally {
      setTitleSaving(false);
    }
  };

  /* ------------------------------- Autosave (Desc) ------------------------- */
  useEffect(() => {
    if (!work || !canEdit || typeof onSaveDescription !== "function") return;
    if (description.trim() === (lastSavedDescRef.current || "").trim()) return;

    if (descTimerRef.current) clearTimeout(descTimerRef.current);
    descTimerRef.current = setTimeout(async () => {
      try {
        setDescSaving(true);
        await onSaveDescription(description);
        lastSavedDescRef.current = description;
      } finally {
        setDescSaving(false);
      }
    }, DEBOUNCE_MS);

    return () => descTimerRef.current && clearTimeout(descTimerRef.current);
  }, [description, canEdit, onSaveDescription, work]);

  const onDescriptionBlur = async () => {
    if (!canEdit || typeof onSaveDescription !== "function") return;
    if (description.trim() === (lastSavedDescRef.current || "").trim()) return;
    if (descTimerRef.current) clearTimeout(descTimerRef.current);
    try {
      setDescSaving(true);
      await onSaveDescription(description);
      lastSavedDescRef.current = description;
    } finally {
      setDescSaving(false);
    }
  };

  /* ---------------------------- Client & Location -------------------------- */
  const snapClient = work?.clientSnapshot || {};
  // 🔹 prefer the fully-fetched prop client (has complete addresses), then fallback to work.client
  const liveClient = clientProp || work?.client || {};
  const client = Object.keys(snapClient).length ? snapClient : liveClient;

  // Resolve addresses but treat empty objects as no address
  const rawClientAddr = pickClientAddress(snapClient, liveClient);
  const clientAddr = hasAddress(rawClientAddr) ? rawClientAddr : null;

  // Work site address (no fallback to client here)
  const rawWorkAddr = work?.address || null;
  const workAddr = hasAddress(rawWorkAddr) ? rawWorkAddr : null;

  const showDiffBadge = workAddr && clientAddr && !sameAddress(workAddr, clientAddr);

  const clientName = pickClientField(snapClient, liveClient, "name");
  const clientEmail = pickClientField(snapClient, liveClient, "email");
  const clientPhone = pickClientField(snapClient, liveClient, "phone");
  const clientId = snapClient?.id ?? liveClient?.id;

  /* --------------------------------- Render -------------------------------- */
  if (resolving) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">Resolving work number…</div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        No data returned for this item.
      </div>
    );
  }
  if (isLoading || !work) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-1/3 rounded bg-slate-200" />
          <div className="h-24 rounded bg-slate-200" />
          <div className="h-24 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview (Title • Description • Status History) */}
      <Section title="Overview">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: Title + Description (autosave) */}
          <div>
            <div className="text-xs font-medium text-slate-600">Title</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={onTitleBlur}
              disabled={!canEdit}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="Add a clear title…"
            />
            {titleSaving ? <div className="mt-1 text-xs text-slate-500">Saving…</div> : null}

            <div className="mt-4 text-xs font-medium text-slate-600">Description</div>
            <textarea
              rows={7}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={onDescriptionBlur}
              disabled={!canEdit}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="Internal notes or description…"
            />
            {descSaving ? <div className="mt-1 text-xs text-slate-500">Saving…</div> : null}
          </div>

          {/* Right: Status History */}
          <div>
            <div className="mb-2 text-sm font-semibold text-slate-900">Status History</div>
            <StatusTimeline timeline={Array.isArray(work?.statusTimeline) ? work.statusTimeline : []} />
          </div>
        </div>
      </Section>

      {/* Client & Location */}
      <Section
        title="Client & Location"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {work?.createdAt ? <span className="text-xs text-slate-500">Created {fmtDateTime(work.createdAt)}</span> : null}
            {work?.updatedAt ? <span className="text-xs text-slate-500">• Updated {fmtDateTime(work.updatedAt)}</span> : null}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Client Profile */}
          <div className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-2 text-[13px] font-semibold text-slate-900">Client</div>

            {client ? (
              <>
                <div className="space-y-1">
                  {clientId ? (
                    <button
                      type="button"
                      onClick={() => onOpenEntity?.("client", clientId)}
                      aria-label={`Open client: ${clientName || "Client"}`}
                      className="text-base font-semibold text-indigo-700 hover:underline"
                    >
                      {clientName || "—"}
                    </button>
                  ) : (
                    <div className="text-base font-semibold text-slate-900">{clientName || "—"}</div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                    {clientPhone ? (
                      <a href={`tel:${clientPhone}`} className="inline-flex items-center gap-1 hover:text-slate-800">
                        <Phone size={14} /> {clientPhone}
                      </a>
                    ) : null}
                    {clientEmail ? (
                      <a href={`mailto:${clientEmail}`} className="inline-flex items-center gap-1 hover:text-slate-800">
                        <Mail size={14} /> {clientEmail}
                      </a>
                    ) : null}
                  </div>
                </div>

                {/* Address */}
                {clientAddr ? (
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:px-5 md:py-4 border-l-4 border-slate-200">
                    <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      <MapPin size={14} /> Client Address
                    </div>
                    <div className="text-[0.95rem] leading-6 text-slate-800">
                      {multiLineAddress(clientAddr).map((l, i) => (
                        <div key={i}>{l}</div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-500">
                    {loadingClient ? "Loading client address…" : "No client address on file."}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-slate-500">Client not linked.</div>
            )}
          </div>

          {/* Work Location */}
          <div className="rounded-2xl border border-slate-200 p-4" id="work-location">
            <div className="mb-2 text-[13px] font-semibold text-slate-900">Work Location</div>

            <WorkAddressInlineEditor
              initial={workAddr}
              clientAddr={clientAddr}
              canEdit={canEdit && typeof onSaveAddress === "function"}
              onSaveAddress={onSaveAddress}
            />

            {workAddr && clientAddr && showDiffBadge ? (
              <div className="mt-3 inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                Note: Different from client address.
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      {/* Type-specific */}
      {work.type === "task" && (
        <Section title="Task Profile">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="mb-2 text-sm font-medium">Checklist</div>
              {Array.isArray(work?.taskProfile?.checklist) && work.taskProfile.checklist.length ? (
                <ul className="space-y-2">
                  {work.taskProfile.checklist.map((it) => (
                    <li key={it._id || it.id} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={!!it.done} readOnly className="mt-0.5" />
                      <div>
                        <div className={cx("font-medium", it.done && "line-through text-slate-500")}>{it.label}</div>
                        {it.notes ? <div className="text-xs text-slate-500">{it.notes}</div> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-slate-500">No checklist items.</div>
              )}
            </div>
            <div className="space-y-1 text-sm">
              <div>
                Est. Hours:{" "}
                <span className="font-medium">
                  {work?.taskProfile?.estimatedHours != null ? work.taskProfile.estimatedHours : "—"}
                </span>
              </div>
              <div>
                Actual Hours:{" "}
                <span className="font-medium">
                  {work?.taskProfile?.actualHours != null ? work.taskProfile.actualHours : "—"}
                </span>
              </div>
            </div>
          </div>
        </Section>
      )}

      {work.type === "job" && (
        <Section
          title="Job Profile"
          actions={
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{work?.jobProfile?.onsiteRequired ? "Onsite" : "Remote ok"}</span>
              {typeof onSaveJobProfile === "function" && canEdit && (
                <>
                  <button
                    type="button"
                    onClick={() => setEditingJob((v) => !v)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Edit3 size={14} /> {editingJob ? "Cancel" : "Edit"}
                  </button>
                  {editingJob && (
                    <button
                      type="button"
                      onClick={async () => {
                        const payload = {
                          serviceType: jpServiceType || undefined,
                          onsiteRequired: jpOnsite,
                          pricing: {
                            currency: jpCurrency || currency,
                            expectedAmount: jpExpected === "" ? undefined : Number(jpExpected),
                          },
                          sla: {
                            responseBy: jpRespBy ? new Date(jpRespBy).toISOString() : undefined,
                            resolveBy: jpResolveBy ? new Date(jpResolveBy).toISOString() : undefined,
                          },
                          materials: (jpMaterials || []).map((m) => ({
                            name: m.name || "",
                            qty: m.qty === "" || m.qty == null ? undefined : Number(m.qty),
                            unitCost: m.unitCost === "" || m.unitCost == null ? undefined : Number(m.unitCost),
                          })),
                        };
                        await onSaveJobProfile(payload);
                        setEditingJob(false);
                      }}
                      className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
                    >
                      Save
                    </button>
                  )}
                </>
              )}
            </div>
          }
        >
          {!editingJob ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1 text-sm">
                <div>
                  Service Type: <span className="font-medium">{work?.jobProfile?.serviceType || "—"}</span>
                </div>
                <div>
                  Onsite Required: <span className="font-medium">{work?.jobProfile?.onsiteRequired ? "Yes" : "No"}</span>
                </div>
                <div>
                  Currency: <span className="font-medium">{currency}</span>
                </div>
                <div>
                  Expected Amount:{" "}
                  <span className="font-medium">
                    {work?.jobProfile?.pricing?.expectedAmount != null
                      ? fmtMoney(work.jobProfile.pricing.expectedAmount, currency)
                      : "—"}
                  </span>
                </div>
                <div>
                  SLA Response By: <span className="font-medium">{fmtDateTime(work?.jobProfile?.sla?.responseBy)}</span>
                </div>
                <div>
                  SLA Resolve By: <span className="font-medium">{fmtDateTime(work?.jobProfile?.sla?.resolveBy)}</span>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium">Materials</div>
                {Array.isArray(work?.jobProfile?.materials) && work.jobProfile.materials.length ? (
                  <div className="overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left">
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Qty</th>
                          <th className="px-3 py-2">Unit Cost</th>
                          <th className="px-3 py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {work.jobProfile.materials.map((m, i) => (
                          <tr key={`${m.name}-${i}`} className="border-t">
                            <td className="px-3 py-2">{m.name || "—"}</td>
                            <td className="px-3 py-2">{m.qty ?? "—"}</td>
                            <td className="px-3 py-2">
                              {m.unitCost != null ? fmtMoney(m.unitCost, currency) : "—"}
                            </td>
                            <td className="px-3 py-2">
                              {m.qty != null && m.unitCost != null ? fmtMoney(m.qty * m.unitCost, currency) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">No materials.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-xs font-medium text-slate-600">Service Type</div>
                  <input
                    value={jpServiceType}
                    onChange={(e) => setJpServiceType(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={jpOnsite}
                    onChange={(e) => setJpOnsite(e.target.checked)}
                    className="size-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Onsite required
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-medium text-slate-600">Currency</div>
                    <input
                      value={jpCurrency}
                      onChange={(e) => setJpCurrency(e.target.value.toUpperCase())}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-600">Expected Amount</div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={jpExpected}
                      onChange={(e) => setJpExpected(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-600">SLA Response By</div>
                  <input
                    type="datetime-local"
                    value={jpRespBy}
                    onChange={(e) => setJpRespBy(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-600">SLA Resolve By</div>
                  <input
                    type="datetime-local"
                    value={jpResolveBy}
                    onChange={(e) => setJpResolveBy(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Materials</div>
                  <button
                    type="button"
                    onClick={() => setJpMaterials((arr) => [...arr, { name: "", qty: 1, unitCost: 0 }])}
                    className="text-xs text-indigo-700 hover:underline"
                  >
                    Add row
                  </button>
                </div>
                <div className="mt-2 overflow-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left">
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Qty</th>
                        <th className="px-3 py-2">Unit Cost</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jpMaterials.map((m, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">
                            <input
                              value={m.name || ""}
                              onChange={(e) =>
                                setJpMaterials((arr) =>
                                  arr.map((mm, idx) => (idx === i ? { ...mm, name: e.target.value } : mm))
                                )
                              }
                              className="w-full rounded-md border border-slate-200 px-2 py-1"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="1"
                              value={m.qty ?? ""}
                              onChange={(e) =>
                                setJpMaterials((arr) =>
                                  arr.map((mm, idx) => (idx === i ? { ...mm, qty: e.target.value } : mm))
                                )
                              }
                              className="w-24 rounded-md border border-slate-200 px-2 py-1"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={m.unitCost ?? ""}
                              onChange={(e) =>
                                setJpMaterials((arr) =>
                                  arr.map((mm, idx) => (idx === i ? { ...mm, unitCost: e.target.value } : mm))
                                )
                              }
                              className="w-28 rounded-md border border-slate-200 px-2 py-1"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => setJpMaterials((arr) => arr.filter((_, idx) => idx !== i))}
                              className="text-xs text-rose-600 hover:underline"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

/* ---------------------------- Status timeline ----------------------------- */
function StatusTimeline({ timeline = [], limit = 100 }) {
  const [showAll, setShowAll] = useState(false);
  const now = Date.now();
  const items = showAll ? timeline : timeline.slice(-Math.min(limit, 6));

  if (!timeline.length) return <div className="text-sm text-slate-500">No status changes yet.</div>;

  return (
    <>
      <ol className="relative ml-2 border-l border-slate-200">
        {items.map((sp, idx) => {
          const status = prettyStatus(sp?.status || "");
          const start = sp?.startedAt ? new Date(sp.startedAt).getTime() : null;
          const end = sp?.endedAt ? new Date(sp.endedAt).getTime() : null;
          const dur = start ? humanizeDuration((end ?? now) - start) : "";
          const who = sp?.actor?.name || (sp?.actor?.id ? `User ${String(sp.actor.id).slice(-6)}` : "System");
          return (
            <li key={`${sp?.startedAt || idx}-${sp?.status}`} className="mb-4 ml-4">
              <span className="absolute -left-1.5 mt-1.5 inline-flex size-3 rounded-full bg-slate-300 ring-4 ring-white" />
              <div className="text-sm font-medium text-slate-900">{status || "—"}</div>
              <div className="text-xs text-slate-500">
                {who} • {sp?.startedAt ? fmtDateTime(sp.startedAt) : "—"}
                {dur ? ` • ${dur}` : ""}
              </div>
              {sp?.reason ? <div className="mt-1 text-xs text-slate-700">{sp.reason}</div> : null}
            </li>
          );
        })}
      </ol>
      {timeline.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs font-medium text-indigo-700 hover:underline"
        >
          {showAll ? "Show less" : `Show all (${timeline.length})`}
        </button>
      )}
    </>
  );
}
